/**
 * Location state flag set when the template agent navigates away from `/new`
 * after a successful create. TemplateUnsavedPrompt must not block that transition
 * — the abandoned local draft is intentionally discarded.
 *
 * Also holds sessionStorage helpers for create chat persistence + handoff:
 * the create conversation UUID is tracked while on `/new` (survives refresh),
 * then rebound to the saved template id so detail chat loads the same history.
 *
 * Project templates and forms each get their own storage keys via
 * {@link createTemplateAgentCreateStorage}.
 */ export const TEMPLATE_AGENT_CREATE_NAV_STATE_KEY = 'templateAgentCreateNav';
export const templateAgentCreateNavState = {
    [TEMPLATE_AGENT_CREATE_NAV_STATE_KEY]: true
};
export const isTemplateAgentCreateNav = (state)=>Boolean(state && typeof state === 'object' && state[TEMPLATE_AGENT_CREATE_NAV_STATE_KEY] === true);
/** `/templates/projects/new` or `/templates/projects/folder/:folderId/new`. */ export const isProjectTemplateNewPath = (pathname)=>/\/templates\/projects\/(?:folder\/[^/]+\/)?new(?:\/|$)/.test(pathname);
const canUseSessionStorage = ()=>typeof sessionStorage !== 'undefined';
/**
 * Factory for create-flow sessionStorage + Strict Mode handoff replay.
 * Each entity (project templates, forms, …) passes distinct key names.
 */ export const createTemplateAgentCreateStorage = (keys)=>{
    /**
   * Survive React Strict Mode remount only: first consume removes sessionStorage,
   * but the sync remount effect must still see the same handoff id. Cleared on
   * the next microtask so later navigations call getConversations normally.
   */ const strictModeHandoffReplayByTemplateId = new Map();
    const setCreateConversationId = (conversationId)=>{
        if (!canUseSessionStorage() || !conversationId) {
            return;
        }
        sessionStorage.setItem(keys.conversation, conversationId);
    };
    const getCreateConversationId = ()=>{
        if (!canUseSessionStorage()) {
            return null;
        }
        const value = sessionStorage.getItem(keys.conversation);
        return value && value.length > 0 ? value : null;
    };
    const clearCreateConversationId = ()=>{
        if (!canUseSessionStorage()) {
            return;
        }
        sessionStorage.removeItem(keys.conversation);
    };
    const setCreateChatHandoff = (handoff)=>{
        if (!canUseSessionStorage()) {
            return;
        }
        if (!Number.isFinite(handoff.templateId) || handoff.templateId <= 0 || !handoff.conversationId) {
            return;
        }
        strictModeHandoffReplayByTemplateId.delete(handoff.templateId);
        sessionStorage.setItem(keys.handoff, JSON.stringify(handoff));
    };
    const consumeCreateChatHandoff = (templateId)=>{
        const replay = strictModeHandoffReplayByTemplateId.get(templateId);
        if (replay != null) {
            return replay;
        }
        if (!canUseSessionStorage()) {
            return null;
        }
        const raw = sessionStorage.getItem(keys.handoff);
        if (!raw) {
            return null;
        }
        try {
            const parsed = JSON.parse(raw);
            if (parsed.templateId === templateId && typeof parsed.conversationId === 'string' && parsed.conversationId.length > 0) {
                sessionStorage.removeItem(keys.handoff);
                strictModeHandoffReplayByTemplateId.set(templateId, parsed.conversationId);
                // Strict Mode remount is synchronous; clear after this turn so a later
                // mount of the same template id does not skip getConversations.
                queueMicrotask(()=>{
                    strictModeHandoffReplayByTemplateId.delete(templateId);
                });
                return parsed.conversationId;
            }
        } catch (unused) {
            sessionStorage.removeItem(keys.handoff);
        }
        return null;
    };
    const clearCreateChatHandoff = ()=>{
        strictModeHandoffReplayByTemplateId.clear();
        if (!canUseSessionStorage()) {
            return;
        }
        sessionStorage.removeItem(keys.handoff);
    };
    /**
   * After Save (or legacy agent create): bind create conversation to template id
   * and stash a one-shot handoff for the edit chat panel. Best-effort — handoff
   * still set even if PATCH fails so SPA can open the same conversation id.
   */ const handoffCreateChatToTemplate = async (params)=>{
        const conversationId = getCreateConversationId();
        if (!conversationId || params.templateId <= 0) {
            return null;
        }
        try {
            await params.bind({
                conversationId,
                templateId: params.templateId,
                userId: params.userId,
                accountId: params.accountId
            });
        } catch (error) {
            var _params_onBindError;
            (_params_onBindError = params.onBindError) === null || _params_onBindError === void 0 ? void 0 : _params_onBindError.call(params, error);
        }
        setCreateChatHandoff({
            templateId: params.templateId,
            conversationId
        });
        clearCreateConversationId();
        return conversationId;
    };
    return {
        setCreateConversationId,
        getCreateConversationId,
        clearCreateConversationId,
        setCreateChatHandoff,
        consumeCreateChatHandoff,
        clearCreateChatHandoff,
        handoffCreateChatToTemplate
    };
};
const projectCreateStorage = createTemplateAgentCreateStorage({
    conversation: 'templateAgentCreateConversationId',
    handoff: 'templateAgentCreateChatHandoff'
});
export const { setCreateConversationId, getCreateConversationId, clearCreateConversationId, setCreateChatHandoff, consumeCreateChatHandoff, clearCreateChatHandoff, handoffCreateChatToTemplate } = projectCreateStorage;
/**
 * Intentional new-draft entry (New CTA). Clears abandoned create chat so a
 * later `/new` visit does not resume the previous conversation. Refresh of an
 * in-progress `/new` does not call this — sessionStorage id is kept.
 */ export const beginFreshCreateDraft = ()=>{
    clearCreateConversationId();
};
/** Forms-template agent — separate session keys from project templates. */ export const formsCreateStorage = createTemplateAgentCreateStorage({
    conversation: 'formsTemplateAgentCreateConversationId',
    handoff: 'formsTemplateAgentCreateChatHandoff'
});
